import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { getExploreTrailUrl, getTrailUrl } from '@alltrails/cards/utils/cardLinks';
import { useClientNavigationPushState } from '@alltrails/core/hooks/useClientNavigation';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import formatTrailSlug from '@alltrails/trails/utils/formatTrailSlug';
import type { TrailSearchResult } from '@alltrails/types/AlgoliaResults';
import isBrowserHandledClick, { type BrowserHandledClickEvent } from '@/utils/isBrowserHandledClick';

/**
 * Builds the explore trail page URL. Takes the same bare trail slug as the cards' own `getTrailUrl`
 * (no `trail/` prefix), so a card's href and its client-side navigation target cannot drift apart.
 */
export const useExploreTrailUrl = () => {
  const languageRegionCode = useLanguageRegionCode();

  return useCallback((slug: string) => getExploreTrailUrl(slug, undefined /* baseUrl */, languageRegionCode), [languageRegionCode]);
};

const useTrailNavigate = () => {
  const languageRegionCode = useLanguageRegionCode();
  const pushState = useClientNavigationPushState();
  const exploreTrailUrl = useExploreTrailUrl();
  const isMobileBrowser = useSelector((state: { context: { mobileBrowser: boolean } }) => state.context?.mobileBrowser);

  return useCallback(
    (slug: string, event?: BrowserHandledClickEvent) => {
      if (isMobileBrowser) {
        window.open(getTrailUrl(slug, undefined /* baseUrl */, languageRegionCode), '_blank');
      } else if (isBrowserHandledClick(event)) {
        window.open(exploreTrailUrl(slug), '_blank');
      } else {
        pushState(exploreTrailUrl(slug));
      }
    },
    [exploreTrailUrl, isMobileBrowser, languageRegionCode, pushState]
  );
};

/** Fits `useTrailNavigate` to the map's trail cards and pins, which pass the clicked search result */
export const useMapTrailNavigate = () => {
  const trailNavigate = useTrailNavigate();

  return useCallback(
    (trail: TrailSearchResult, event?: BrowserHandledClickEvent) => trailNavigate(formatTrailSlug(trail.slug), event),
    [trailNavigate]
  );
};

export default useTrailNavigate;
